import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { useLayoutEffect, useRef } from 'react'
import { cn } from '../lib/cn.ts'
import { IconAlert } from './Icon.tsx'

/** The input surface: 48px, radius 8, real 1px border, sheet fill, 2px teal ring on focus. */
export const controlClass =
	'w-full rounded-input border border-line bg-sheet px-3.5 text-body text-ink ' +
	'transition-[border-color,box-shadow] duration-(--dur-instant) ' +
	'hover:border-line-strong focus-visible:outline-none focus:border-teal-700 focus:shadow-[0_0_0_1px_var(--color-teal-700)] ' +
	'disabled:bg-canvas-deep disabled:text-ink-2 aria-invalid:border-error aria-invalid:focus:shadow-[0_0_0_1px_var(--color-error)]'

export type FieldShellProps = { label: string; hint?: string; error?: string; className?: string }

/** Label above (always visible; never a placeholder), hint or error below. */
export function FieldShell({
	id,
	label,
	hint,
	error,
	className,
	children,
}: FieldShellProps & { id: string; children: ReactNode }) {
	return (
		<div className={cn('flex flex-col gap-1.5', className)}>
			<label htmlFor={id} className="text-small font-[560] text-ink">
				{label}
			</label>
			{children}
			{hint && !error && (
				<p id={`${id}-hint`} className="text-small text-ink-2">
					{hint}
				</p>
			)}
			{error && (
				<p
					id={`${id}-error`}
					role="alert"
					className="flex items-start gap-1.5 text-small font-medium text-error animate-fade-in"
				>
					<IconAlert className="mt-px size-4 shrink-0" />
					{error}
				</p>
			)}
		</div>
	)
}

export const describedBy = (id: string, hint?: string, error?: string) =>
	error ? `${id}-error` : hint ? `${id}-hint` : undefined

export function TextField({
	label,
	hint,
	error,
	className,
	id,
	...rest
}: FieldShellProps & InputHTMLAttributes<HTMLInputElement> & { id: string }) {
	return (
		<FieldShell id={id} label={label} hint={hint} error={error} className={className}>
			<input
				id={id}
				aria-invalid={!!error || undefined}
				aria-describedby={describedBy(id, hint, error)}
				className={cn(controlClass, 'h-12')}
				{...rest}
			/>
		</FieldShell>
	)
}

export function TextAreaField({
	label,
	hint,
	error,
	className,
	id,
	...rest
}: FieldShellProps & TextareaHTMLAttributes<HTMLTextAreaElement> & { id: string }) {
	return (
		<FieldShell id={id} label={label} hint={hint} error={error} className={className}>
			<textarea
				id={id}
				aria-invalid={!!error || undefined}
				aria-describedby={describedBy(id, hint, error)}
				className={cn(controlClass, 'min-h-28 resize-y py-3 leading-relaxed')}
				{...rest}
			/>
		</FieldShell>
	)
}

/**
 * Editable text that reads as text: one ruled line under it at rest (a worksheet line), the
 * full field on hover and focus. For editing inside a sheet; pass `sheet-type` when it is
 * sheet content.
 */
const inline =
	'min-h-11 w-full rounded-input border border-transparent border-b-line bg-transparent px-2 py-2 text-ink ' +
	'transition-[border-color,background-color,box-shadow] duration-(--dur-instant) ' +
	'hover:border-line hover:bg-sheet focus-visible:outline-none focus:border-teal-700 focus:bg-sheet focus:shadow-[0_0_0_1px_var(--color-teal-700)]'

export function InlineInput({
	label,
	className,
	id,
	...rest
}: { label: string; id: string } & InputHTMLAttributes<HTMLInputElement>) {
	return <input id={id} aria-label={label} className={cn(inline, className)} {...rest} />
}

const FIELD_SIZING = typeof CSS !== 'undefined' && CSS.supports('field-sizing', 'content')

export function InlineTextArea({
	label,
	className,
	id,
	...rest
}: { label: string; id: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
	const ref = useRef<HTMLTextAreaElement>(null)
	// `field-sizing: content` grows it in Chromium; Safari and Firefox would clip a long title to one row.
	useLayoutEffect(() => {
		const el = ref.current
		if (!el || FIELD_SIZING) return
		el.style.height = 'auto'
		el.style.height = `${el.scrollHeight + 2}px`
	}, [rest.value])
	return (
		<textarea
			ref={ref}
			id={id}
			aria-label={label}
			rows={1}
			className={cn(inline, 'resize-none leading-relaxed', className)}
			{...rest}
		/>
	)
}
